import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'fitify-types/src/zod'
import { toast } from 'fitify-ui/src/Toast'
import { fetchRedeemVoucher } from 'fitify-ui-onboarding/src/api/nextApi'
import { useAuthUserContext } from 'fitify-ui-onboarding/src/contexts/AuthUserContext'
import { useRouter } from 'fitify-ui-onboarding/src/hooks/useRouter'
import type { AppUser } from 'fitify-ui-onboarding/src/types/app'
import { encodeQuery } from 'fitify-ui-onboarding/src/utils/query'
import { useTranslation } from 'next-i18next/pages'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { APP_PAGES } from 'data/routes'
import { useLogout } from 'hooks/useLogout'

type FormValues = {
  voucherCode: string
}

export const useRedeemVoucher = () => {
  const { authUser, isAuthLoading, isLoggedIn, userProfile } =
    useAuthUserContext()
  const { handleLogout } = useLogout()
  const router = useRouter()
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const { control, formState, handleSubmit, setError } = useForm<FormValues>({
    defaultValues: {
      voucherCode: '',
    },
    mode: 'onTouched',
    resolver: zodResolver(
      z.object({
        voucherCode: z
          .string({ message: t('common_required') })
          .trim()
          .length(8, { message: t('redeem_error_voucher_invalid') }),
      })
    ),
  })

  const isPageLoading =
    isAuthLoading || !isLoggedIn || userProfile?.subscription?.active

  useEffect(() => {
    if (isAuthLoading) {
      return
    }

    if (userProfile?.subscription?.active) {
      void router.replace(APP_PAGES.success.path)
      return
    }

    if (!isLoggedIn) {
      void router.push(
        encodeQuery(APP_PAGES.signup.path, {
          next: encodeQuery(APP_PAGES.redeem.path),
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, isAuthLoading])

  const onSubmit = useCallback(
    async (formValues: FormValues) => {
      setIsLoading(true)
      try {
        const data = await fetchRedeemVoucher(formValues.voucherCode)

        if (!data.valid) {
          setError('voucherCode', {
            message: t('redeem_error_voucher_invalid'),
          })
          toast({
            type: 'error',
            content: t('redeem_error_voucher_invalid'),
          })
          setIsLoading(false)
          return
        }

        toast({
          type: 'success',
          content: t('redeem_success'),
        })

        void router.push(encodeQuery(APP_PAGES.success.path), undefined, {
          shallow: true,
        })
      } catch {
        toast({
          type: 'error',
          content: t('common_error_generic_message'),
        })
        setIsLoading(false)
      }
    },
    [router, setError, t]
  )

  const user = useMemo<AppUser | undefined>(() => {
    if (authUser) {
      return {
        uid: authUser.uid,
        displayName: authUser.displayName ?? undefined,
        email: authUser.email ?? undefined,
        onLogout: handleLogout,
      }
    }
    return undefined
  }, [authUser, handleLogout])

  const form = useMemo(() => {
    return {
      state: formState,
      onSubmit: handleSubmit(onSubmit),
    }
  }, [formState, handleSubmit, onSubmit])

  return {
    control,
    form,
    isLoading,
    isPageLoading,
    user,
  }
}
